const DEFAULT_TARGET_SAMPLE_RATE = 16000;
const DEFAULT_CHANNELS = 1;

function isLocalhostHost(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function getAudioConstraints(targetSampleRate, channelCount) {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getSupportedConstraints !== 'function') {
    return { audio: true };
  }

  const supported = navigator.mediaDevices.getSupportedConstraints();
  const audio = {};

  if (supported.channelCount) {
    audio.channelCount = { ideal: channelCount };
  }

  if (supported.sampleRate) {
    audio.sampleRate = { ideal: targetSampleRate };
  }

  if (supported.echoCancellation) {
    audio.echoCancellation = true;
  }

  if (supported.noiseSuppression) {
    audio.noiseSuppression = true;
  }

  if (supported.autoGainControl) {
    audio.autoGainControl = true;
  }

  return {
    audio: Object.keys(audio).length > 0 ? audio : true
  };
}

function shouldRetryWithBasicAudio(error) {
  const errorName = error && error.name ? error.name : '';
  return errorName === 'OverconstrainedError'
    || errorName === 'ConstraintNotSatisfiedError'
    || errorName === 'TypeError';
}

export function normalizeRecorderError(error) {
  const errorName = error && error.name ? error.name : '';

  switch (errorName) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return new Error('没有拿到麦克风权限，请在浏览器地址栏允许麦克风访问后再试一次。');
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return new Error('没有检测到可用麦克风，请连接麦克风后重试。');
    case 'NotReadableError':
    case 'TrackStartError':
      return new Error('麦克风当前被其他应用或标签页占用，请关闭占用后重试。');
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return new Error('浏览器当前不接受这组录音参数，已切换兼容模式；请再试一次。');
    case 'AbortError':
      return new Error('麦克风启动被浏览器中断，请重新点击语音输入。');
    case 'TypeError':
      return new Error('当前页面无法启动麦克风，请确认网页运行在 HTTPS 或 localhost 环境。');
    case 'NotSupportedError':
      return new Error('当前浏览器不支持这套录音能力，请尝试最新版 Firefox、Chrome 或 Edge。');
    default:
      if (error && error.message) {
        return new Error(error.message);
      }
      return new Error('麦克风暂时无法使用，请稍后再试。');
  }
}

function mergeBuffers(chunks) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Float32Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return merged;
}

function downsampleBuffer(buffer, inputSampleRate, outputSampleRate) {
  if (outputSampleRate >= inputSampleRate) {
    return buffer;
  }

  const ratio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLength);

  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
    let accum = 0;
    let count = 0;

    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
      accum += buffer[i];
      count += 1;
    }

    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }

  return result;
}

function floatTo16BitPCM(view, offset, input) {
  for (let i = 0; i < input.length; i += 1, offset += 2) {
    const sample = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
}

function writeString(view, offset, value) {
  for (let i = 0; i < value.length; i += 1) {
    view.setUint8(offset + i, value.charCodeAt(i));
  }
}

function encodeWav(samples, sampleRate, channelCount) {
  const bytesPerSample = 2;
  const dataLength = samples.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channelCount * bytesPerSample, true);
  view.setUint16(32, channelCount * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true);
  floatTo16BitPCM(view, 44, samples);

  return new Blob([view], { type: 'audio/wav' });
}

export class VoiceRecorder {
  constructor({ targetSampleRate = DEFAULT_TARGET_SAMPLE_RATE, channelCount = DEFAULT_CHANNELS } = {}) {
    this.targetSampleRate = targetSampleRate;
    this.channelCount = channelCount;
    this.audioContext = null;
    this.mediaStream = null;
    this.sourceNode = null;
    this.processorNode = null;
    this.silenceNode = null;
    this.chunks = [];
    this.inputSampleRate = targetSampleRate;
    this.startedAt = 0;
    this.isRecording = false;
    this.onLevel = null;
  }

  async start({ onLevel } = {}) {
    if (this.isRecording) {
      throw new Error('Recorder is already running.');
    }

    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      const hostname = window.location && window.location.hostname ? window.location.hostname : '';
      if (!isLocalhostHost(hostname)) {
        throw new Error('当前页面不是安全环境，麦克风只能在 HTTPS 或 localhost 下使用。');
      }
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('当前浏览器不支持麦克风访问，请使用最新版 Firefox、Chrome 或 Edge。');
    }

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) {
      throw new Error('当前浏览器不支持录音音频处理，请升级浏览器后重试。');
    }

    this.chunks = [];
    this.onLevel = typeof onLevel === 'function' ? onLevel : null;

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia(
        getAudioConstraints(this.targetSampleRate, this.channelCount)
      );
    } catch (error) {
      if (shouldRetryWithBasicAudio(error)) {
        try {
          this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (fallbackError) {
          throw normalizeRecorderError(fallbackError);
        }
      } else {
        throw normalizeRecorderError(error);
      }
    }

    try {
      this.audioContext = new AudioContextCtor();
      this.inputSampleRate = this.audioContext.sampleRate;
      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream);

      const createScriptProcessor = this.audioContext.createScriptProcessor || this.audioContext.createJavaScriptNode;
      if (typeof createScriptProcessor !== 'function') {
        throw new Error('当前浏览器缺少录音处理节点，请尝试最新版 Firefox、Chrome 或 Edge。');
      }

      this.processorNode = createScriptProcessor.call(this.audioContext, 4096, 1, 1);
      this.silenceNode = this.audioContext.createGain();
      this.silenceNode.gain.value = 0;

      this.processorNode.onaudioprocess = (event) => {
        if (!this.isRecording) {
          return;
        }

        const channelData = event.inputBuffer.getChannelData(0);
        this.chunks.push(new Float32Array(channelData));

        if (this.onLevel) {
          let sum = 0;
          for (let i = 0; i < channelData.length; i += 1) {
            const sample = channelData[i];
            sum += sample * sample;
          }

          const rms = Math.sqrt(sum / channelData.length);
          const normalizedLevel = Math.max(0, Math.min(1, rms * 4.5));
          this.onLevel(normalizedLevel);
        }
      };

      this.sourceNode.connect(this.processorNode);
      this.processorNode.connect(this.silenceNode);
      this.silenceNode.connect(this.audioContext.destination);

      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    } catch (error) {
      await this.stop({ cancel: true });
      throw normalizeRecorderError(error);
    }

    this.startedAt = Date.now();
    this.isRecording = true;
  }

  getDurationMs() {
    return this.startedAt ? Date.now() - this.startedAt : 0;
  }

  async stop({ cancel = false } = {}) {
    if (!this.audioContext && !this.mediaStream) {
      return null;
    }

    this.isRecording = false;
    this.onLevel = null;

    const recordedChunks = this.chunks.slice();
    const inputSampleRate = this.inputSampleRate;
    this.chunks = [];

    if (this.processorNode) {
      this.processorNode.onaudioprocess = null;
      this.processorNode.disconnect();
      this.processorNode = null;
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.silenceNode) {
      this.silenceNode.disconnect();
      this.silenceNode = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.audioContext) {
      const closingContext = this.audioContext;
      this.audioContext = null;
      await closingContext.close();
    }

    this.startedAt = 0;

    if (cancel || recordedChunks.length === 0) {
      return null;
    }

    const merged = mergeBuffers(recordedChunks);
    const downsampled = downsampleBuffer(merged, inputSampleRate, this.targetSampleRate);
    return encodeWav(downsampled, this.targetSampleRate, this.channelCount);
  }
}

